import { Box, Icon } from "@outschool/backpack";
import { fasPlayCircle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { Video } from "@outschool/ui-legacy-component-library";
import { SystemStyleObject } from "@styled-system/css";
import React from "react";

interface SimpleVideoProps {
  title?: string;
  src: string;
  poster?: string | undefined;
  onlyPlayButton?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  onLoadedData?: () => void;
  showControls?: boolean;
  videoRef?: any;
  onClick?: () => void;
  sx?: SystemStyleObject;
  videoSx?: SystemStyleObject;
}

export default function SimpleVideo({
  title,
  src,
  poster,
  onlyPlayButton,
  autoPlay,
  muted,
  onLoadedData,
  showControls = true,
  onClick,
  videoRef,
  sx,
  videoSx,
}: SimpleVideoProps) {
  const { t } = useTranslation("ui-components-website\\SimpleVideo");
  const videoContentRef = React.useRef(null);
  const [playing, setIsPlaying] = React.useState(!!autoPlay);

  const handlePlayClick = () => {
    (videoRef || videoContentRef).current?.play();
    setIsPlaying(true);
    onClick && onClick();
  };

  return (
    <Box
      sx={[
        {
          position: "relative",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          // Create a wrapper div to force a 4:3 ratio
          width: "100%",
          paddingBottom: "75%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Video
          videoRef={videoRef || videoContentRef}
          src={src}
          controls={showControls && playing}
          controlsList="nodownload" // React scraps this but after React 16, this should be good.
          preload="auto"
          poster={poster}
          playsInline={true}
          autoPlay={autoPlay}
          muted={muted}
          onLoadedData={onLoadedData}
          sx={{
            width: "100%",

            boxSizing: "border-box",
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            height: "100%",
            ...videoSx,
          }}
        />
      </Box>
      {!playing ? (
        // Use backgroundImage to act as the 'Poster' for our video.
        // This gives us more control over the image displayed before video displayed
        // and works around the weird spec debate about "poster" vs "first frame" in the video tag spec
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            color: "common.white",
            backgroundImage: !!poster ? `url("${poster}")` : undefined,
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            backgroundBlendMode: "overlay",
          }}
        >
          <div />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {onlyPlayButton ? null : (
              <Box
                sx={{
                  marginBottom: "2em",
                }}
              >
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {title}
                </Box>
              </Box>
            )}
            <Box sx={!onlyPlayButton && { marginBottom: "1em" }}>
              <Icon
                icon={fasPlayCircle}
                sx={{
                  fontSize: "7em",
                  opacity: "0.7",
                }}
                onClick={handlePlayClick}
              />
            </Box>
            {onlyPlayButton ? null : (
              <div>
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {t`Press Play`}
                </Box>
              </div>
            )}
          </Box>
          <div />
        </Box>
      ) : null}
    </Box>
  );
}
